import { useMemo, useSyncExternalStore } from "react";
import { getAgenteContabilidadeClientContext } from "../../../utils/agenteContabilidadeAccess";
import {
  DEFAULT_TAX_REGIME,
  getDefaultExpenseAliquotas,
  getDefaultTaxAliquotas,
} from "../services/dreService";
import {
  importarArquivo as apiImportarArquivo,
  importarViaSienge as apiImportarViaSienge,
  listarCentrosCustoSienge as apiListarCentrosCustoSienge,
  logContabilidadeApiError,
  listarEmpreendimentos as apiListarEmpreendimentos,
  listarImportacoes as apiListarImportacoes,
} from "../services/agenteContabilidadeApiService";

export const AGENTE_CONTABILIDADE_PRODUCT_ID = 9;

const listeners = new Set();
const stateCache = new Map();

function emitChange() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getTenantContext() {
  const context = getAgenteContabilidadeClientContext();
  const clientId = Number(context.idCliente || 0);

  return {
    clientId,
    userEmail: context.email || "",
    nomeCliente: context.nomeCliente || "",
  };
}

function getStorageKey(clientId) {
  return `agentecontabilidade:${clientId || "anon"}:prefs:v1`;
}

function createDefaultState(clientId) {
  return {
    version: 2,
    ClienteID: clientId || null,
    IdProduto: AGENTE_CONTABILIDADE_PRODUCT_ID,
    backendCatalog: {
      empreendimentos: [],
      importacoes: [],
      lastSyncAt: null,
    },
    taxConfig: {
      regime: DEFAULT_TAX_REGIME,
      aliquotas: getDefaultTaxAliquotas(),
    },
    despesasConfig: getDefaultExpenseAliquotas(),
    ui: {
      welcomeDismissed: false,
    },
  };
}

function readStateForClient(clientId) {
  const storageKey = getStorageKey(clientId);
  if (stateCache.has(storageKey)) {
    return stateCache.get(storageKey);
  }

  const fallback = createDefaultState(clientId);

  try {
    const rawValue = localStorage.getItem(storageKey);
    if (!rawValue) {
      stateCache.set(storageKey, fallback);
      return fallback;
    }

    const parsed = JSON.parse(rawValue);
    const hydratedState = {
      ...fallback,
      taxConfig: {
        ...fallback.taxConfig,
        ...(parsed.taxConfig || {}),
      },
      despesasConfig: {
        ...fallback.despesasConfig,
        ...(parsed.despesasConfig || {}),
      },
      ui: {
        ...fallback.ui,
        ...(parsed.ui || {}),
      },
    };

    stateCache.set(storageKey, hydratedState);
    return hydratedState;
  } catch (error) {
    stateCache.set(storageKey, fallback);
    return fallback;
  }
}

function writeStateForClient(clientId, nextState) {
  const storageKey = getStorageKey(clientId);
  stateCache.set(storageKey, nextState);
  localStorage.setItem(
    storageKey,
    JSON.stringify({
      taxConfig: nextState.taxConfig,
      despesasConfig: nextState.despesasConfig,
      ui: nextState.ui,
    })
  );
  emitChange();
}

export function useAgenteContabilidadeStore() {
  const tenant = getTenantContext();
  const clientId = tenant.clientId;

  const state = useSyncExternalStore(
    subscribe,
    () => readStateForClient(clientId),
    () => createDefaultState(clientId)
  );

  const actions = useMemo(() => {
    function setState(updater) {
      const currentState = readStateForClient(clientId);
      const nextState =
        typeof updater === "function" ? updater(currentState) : updater;

      if (nextState === currentState) {
        return currentState;
      }

      writeStateForClient(clientId, nextState);
      return nextState;
    }

    async function refreshBackendCatalog() {
      try {
        const [importacoes, empreendimentos] = await Promise.all([
          apiListarImportacoes(),
          apiListarEmpreendimentos(),
        ]);

        setState((currentState) => ({
          ...currentState,
          backendCatalog: {
            importacoes: Array.isArray(importacoes) ? importacoes : [],
            empreendimentos: Array.isArray(empreendimentos) ? empreendimentos : [],
            lastSyncAt: new Date().toISOString(),
          },
        }));

        return { importacoes, empreendimentos };
      } catch (error) {
        logContabilidadeApiError("catalogo", error);
        throw error;
      }
    }

    async function refreshBackendEmpreendimentos() {
      try {
        const empreendimentos = await apiListarEmpreendimentos();

        setState((currentState) => ({
          ...currentState,
          backendCatalog: {
            ...currentState.backendCatalog,
            empreendimentos: Array.isArray(empreendimentos) ? empreendimentos : [],
            lastSyncAt: new Date().toISOString(),
          },
        }));

        return { empreendimentos };
      } catch (error) {
        logContabilidadeApiError("empreendimentos", error);
        throw error;
      }
    }

    async function saveImportedFile(_fileName, _records, arquivo) {
      let backendResult = null;

      if (arquivo) {
        backendResult = await apiImportarArquivo(arquivo);
      }

      await refreshBackendCatalog().catch(() => {});

      return {
        totalRecords: backendResult?.totalRegistros || 0,
        newRecords: backendResult?.novosRegistros || 0,
        updatedRecords: backendResult?.registrosAtualizados || 0,
        unchangedRecords: backendResult?.registrosIgnorados || 0,
        backendResult,
      };
    }

    async function listSiengeCostCenters(options) {
      return apiListarCentrosCustoSienge(options);
    }

    async function importFromSienge(payload) {
      const backendResult = await apiImportarViaSienge(payload);
      await refreshBackendCatalog().catch(() => {});

      return {
        totalRecords: backendResult?.totalRegistros || 0,
        newRecords: backendResult?.novosRegistros || 0,
        updatedRecords: backendResult?.registrosAtualizados || 0,
        unchangedRecords: backendResult?.registrosIgnorados || 0,
        backendResult,
      };
    }

    function dismissWelcomeModal() {
      setState((currentState) => {
        if (currentState.ui?.welcomeDismissed) {
          return currentState;
        }

        return {
          ...currentState,
          ui: {
            ...(currentState.ui || {}),
            welcomeDismissed: true,
          },
        };
      });
    }

    function saveTaxConfig(regime, aliquotas) {
      setState((currentState) => ({
        ...currentState,
        taxConfig: {
          regime,
          aliquotas: { ...aliquotas },
        },
      }));
    }

    function saveDespesasConfig(aliquotas) {
      setState((currentState) => ({
        ...currentState,
        despesasConfig: { ...aliquotas },
      }));
    }

    return {
      dismissWelcomeModal,
      refreshBackendCatalog,
      refreshBackendEmpreendimentos,
      importFromSienge,
      listSiengeCostCenters,
      saveDespesasConfig,
      saveImportedFile,
      saveTaxConfig,
      setState,
    };
  }, [clientId]);

  return {
    tenant,
    state,
    actions,
  };
}
